import { Button as GsButton, ButtonSpinner, ButtonText } from '@gluestack-ui/themed';
import type { ReactNode } from 'react';
import { CONTROL_HEIGHT } from '../../../styles/tokens';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'selected' | 'ghost' | 'danger' | 'dangerOutline';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps {
  readonly children: ReactNode;
  readonly onPress?: () => void;
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly isDisabled?: boolean;
  readonly isLoading?: boolean;
  /** Announced while `isLoading`, so assistive tech reports progress rather than silence. */
  readonly loadingLabel?: string;
  readonly isFullWidth?: boolean;
  readonly accessibilityLabel?: string;
  readonly testID?: string;
}

/** Maps our semantic variants onto Gluestack's action/variant pair. */
const VARIANT_PROPS = {
  primary: { action: 'primary', variant: 'solid' },
  secondary: { action: 'secondary', variant: 'outline' },
  outline: { action: 'secondary', variant: 'outline' },
  selected: { action: 'secondary', variant: 'outline' },
  ghost: { action: 'primary', variant: 'link' },
  danger: { action: 'negative', variant: 'solid' },
  dangerOutline: { action: 'negative', variant: 'outline' },
} as const;

/**
 * `secondary` and `outline` share one neutral bordered style: in dense tooling the only
 * coloured button on screen should be the primary action, so everything else stays grey.
 */
const NEUTRAL_BUTTON = {
  bg: '$backgroundLight0',
  borderWidth: 1,
  borderColor: '$borderLight300',
  '$hover-bg': '$backgroundLight50',
  '$hover-borderColor': '$textLight500',
  '$active-bg': '$backgroundLight100',
  '$dark-bg': '$backgroundDark800',
  '$dark-borderColor': '$borderDark600',
  '$dark-hover-bg': '$backgroundDark700',
} as const;

/** The open choice in a button group: neutral and filled, so only the real primary action is blue. */
const SELECTED_BUTTON = {
  bg: '$backgroundLight100',
  borderColor: '$textLight500',
  '$hover-bg': '$backgroundLight100',
  '$dark-bg': '$backgroundDark600',
  '$dark-hover-bg': '$backgroundDark600',
} as const;

const NEUTRAL_TEXT = { color: '$textLight800', '$dark-color': '$textDark100' } as const;

const SIZE_HEIGHTS: Readonly<Record<ButtonSize, number>> = CONTROL_HEIGHT;

export function Button({
  children,
  onPress,
  variant = 'primary',
  size = 'md',
  isDisabled = false,
  isLoading = false,
  loadingLabel = 'Loading',
  isFullWidth = false,
  accessibilityLabel,
  testID,
}: ButtonProps): React.JSX.Element {
  const { action, variant: gsVariant } = VARIANT_PROPS[variant];
  const isNeutral = variant === 'secondary' || variant === 'outline' || variant === 'selected';

  return (
    <GsButton
      action={action}
      variant={gsVariant}
      size={size}
      // A loading button must not accept a second submission.
      isDisabled={isDisabled || isLoading}
      onPress={onPress}
      testID={testID}
      accessibilityLabel={accessibilityLabel}
      aria-busy={isLoading}
      minHeight={SIZE_HEIGHTS[size]}
      borderRadius="$md"
      px={size === 'sm' ? '$2' : '$3'}
      {...(isNeutral ? NEUTRAL_BUTTON : {})}
      {...(variant === 'selected' ? SELECTED_BUTTON : {})}
      {...(variant === 'selected' ? { 'aria-pressed': true } : {})}
      style={{
        transition: 'background-color 120ms ease, border-color 120ms ease',
      }}
      {...(isFullWidth ? { width: '100%' } : {})}
    >
      {isLoading ? (
        <>
          <ButtonSpinner mr="$2" />
          <ButtonText size="sm">{loadingLabel}</ButtonText>
        </>
      ) : (
        <ButtonText
          size={size === 'lg' ? 'md' : 'sm'}
          style={{ fontWeight: 600 }}
          {...(isNeutral ? NEUTRAL_TEXT : {})}
        >
          {children}
        </ButtonText>
      )}
    </GsButton>
  );
}
